import { useEffect, useState } from 'react';
import { CalendarIcon } from 'lucide-react';
import { id as idLocale } from 'date-fns/locale';
import DatePeriodNavigation from '@/components/DatePeriodNavigation';
import { transactionPeriod } from '@/lib/transaction-period.mjs';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const parse = value => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return undefined;
  const date = new Date(`${value}T00:00:00`);
  return Number.isFinite(date.getTime()) && iso(date) === value ? date : undefined;
};
const iso = date => `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
const labelDate = value => new Intl.DateTimeFormat('id-ID', {day:'numeric',month:'short',year:'numeric'}).format(parse(value));

export function quickDatePeriods(now = new Date()) {
  const today = transactionPeriod('today', now);
  const days = count => { const from = new Date(`${today.start}T00:00:00Z`); from.setUTCDate(from.getUTCDate() - count + 1); return {start:from.toISOString().slice(0,10),end:today.end}; };
  return [['today','Hari ini',today],['yesterday','Kemarin',transactionPeriod('yesterday',now)],
    ['7-days','7 Hari',days(7)],['30-days','30 Hari',days(30)],
    ['this-month','Bulan Ini',transactionPeriod('this-month',now)],['last-month','Bulan Lalu',transactionPeriod('last-month',now)]];
}

// One compact calendar for transaction/purchase filters, reusing shared arrows.
export default function DatePeriodPicker({ start, end, onChange, allowAll = false, label = 'Periode tanggal', testId = 'date-picker' }) {
  const [open,setOpen] = useState(false);
  const [draft,setDraft] = useState({start:start || '',end:end || ''});
  const [month,setMonth] = useState(() => parse(start) || new Date());
  const [compact,setCompact] = useState(() => !!window.matchMedia?.('(max-width: 767px)').matches);
  useEffect(() => {
    const media=window.matchMedia?.('(max-width: 767px)'); if (!media) return;
    const change=e=>setCompact(e.matches);media.addEventListener('change',change);
    return ()=>media.removeEventListener('change',change);
  },[]);
  const valid = !!parse(draft.start) && !!parse(draft.end) && draft.start<=draft.end;
  const rangeText = !start && !end && allowAll ? 'Semua tanggal' : parse(start) && parse(end)
    ? start===end ? labelDate(start) : `${labelDate(start)} — ${labelDate(end)}` : 'Pilih tanggal';
  const changeOpen = value => {
    if (value) {setDraft({start:start || '',end:end || ''});setMonth(parse(start) || new Date());}
    setOpen(value);
  };
  const apply = range => {onChange(range);setOpen(false);};
  return <DatePeriodNavigation start={start} end={end} onChange={apply} label={label} testId={testId}>
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" data-testid={`${testId}-btn`} aria-label={`${label}: ${rangeText}`} title={rangeText}
          className="h-10 min-w-0 gap-2 border-ui-line bg-ui-card px-3 hover:bg-ui-card">
          <CalendarIcon className="h-4 w-4 shrink-0 text-brand-readable" /><span className="max-w-[260px] truncate text-sm">{rangeText}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-auto max-w-[calc(100vw-1rem)] border-ui-line bg-ui-card p-0 text-ui-text" data-testid={`${testId}-calendar`}>
        <div className="flex max-w-[560px] flex-wrap gap-1.5 border-b border-ui-line p-3">
          {quickDatePeriods().map(([key,text,range])=><Button type="button" key={key} variant="outline" data-testid={`${testId}-preset-${key}`}
            aria-pressed={start===range.start && end===range.end} onClick={()=>apply(range)} className="h-7 border-ui-line px-2 text-xs">{text}</Button>)}
          {allowAll && <Button type="button" variant="outline" data-testid={`${testId}-preset-all`} onClick={()=>apply({start:'',end:''})} className="h-7 border-ui-line px-2 text-xs">Semua</Button>}
        </div>
        <Calendar mode="range" selected={{from:parse(draft.start),to:parse(draft.end)}} month={month} onMonthChange={setMonth} numberOfMonths={compact?1:2} locale={idLocale}
          onSelect={range=>setDraft({start:range?.from?iso(range.from):'',end:range?.to?iso(range.to):range?.from?iso(range.from):''})} />
        <div className="space-y-2 border-t border-ui-line p-3">
          <div className="grid grid-cols-2 gap-2">
            <label htmlFor={`${testId}-start`} className="min-w-0 text-xs text-ui-muted">Dari
              <Input id={`${testId}-start`} data-testid={`${testId}-start`} type="date" value={draft.start} className="mt-1 min-w-0 bg-ui-card"
                onChange={e=>{setDraft(v=>({...v,start:e.target.value}));if(parse(e.target.value))setMonth(parse(e.target.value));}} />
            </label>
            <label htmlFor={`${testId}-end`} className="min-w-0 text-xs text-ui-muted">Sampai
              <Input id={`${testId}-end`} data-testid={`${testId}-end`} type="date" value={draft.end} className="mt-1 min-w-0 bg-ui-card" onChange={e=>setDraft(v=>({...v,end:e.target.value}))} />
            </label>
          </div>
          {!valid && <p role="alert" className="text-xs text-danger-readable">Isi tanggal awal dan akhir dengan urutan yang benar.</p>}
          <div className="flex justify-end gap-2"><Button type="button" variant="outline" className="h-8" onClick={()=>setOpen(false)}>Batal</Button>
            <Button type="button" className="h-8" disabled={!valid} data-testid={`${testId}-apply`} onClick={()=>apply(draft)}>Terapkan</Button></div>
        </div>
      </PopoverContent>
    </Popover>
  </DatePeriodNavigation>;
}
